<template>
    <div
        ref="page"
        :style="pageStyle"
        class="d-flex flex-column justify-content-center position-relative"
    >
        <canvas
            v-show="rendered"
            ref="canvas"
            class="mx-auto"
            v-bind="canvasAttrs"
        />

        <div
            ref="data-layer"
            class="pdfjs__text-layer pdfjs__annotation-layer mx-auto"
            :style="canvasAttrs.style"
        />

        <div
            v-if="!rendered"
            class="d-flex align-items-center justify-content-center bg-light mx-auto"
            :style="canvasAttrs.style"
        >
            <loading-spinner :width="70" />
        </div>
    </div>
</template>

<script>
import { toRaw } from "vue"
import { Bus } from "front-toolkit/lib/bus"
import debounce from "lodash/debounce"
import isNumber from "lodash/isNumber"
import LoadingSpinner from "~/components/LoadingSpinner"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

import Semaphore from "~/lib/Semaphore"

class LinkService {
    constructor({ bus }) {
        this.bus = bus

        // All links will open a new tab
        this.externalLinkTarget = 2
    }

    addLinkAttributes(link, url) {
        link.href = url
        link.target = "_blank"
    }

    getDestinationHash() {
        return ""
    }

    goToDestination(destination) {
        this.bus.emit("goto-destination", destination)
    }

    navigateTo(nav) {
        const [pageRef] = nav
        if (isNumber(pageRef)) {
            this.bus.emit("goto-page", pageRef)
        } else {
            this.bus.emit("goto-destination", nav)
        }
    }
}

const overscale = 2

// Render pages one by one
const semaphore = new Semaphore(1)

export default {
    props: {
        pdfjs: Object,
        pdf: Object,
        number: Number,
        visible: Boolean,
        bodyWidth: Number,
        bodyHeight: Number,
        fit: String,
    },

    emits: ["goto-page", "goto-destination"],

    setup() {
        const { listen } = useListenerManager()
        return { listen }
    },

    data() {
        return {
            rendered: false,
            width: 0,
            height: 0,
            devicePixelRatio: null,
        }
    },

    computed: {
        pageStyle() {
            if (this.height) {
                return {
                    minHeight: `${Math.ceil(this.height / overscale)}px`,
                }
            } else {
                return {
                    minHeight: `100%`,
                }
            }
        },

        canvasAttrs() {
            const w = Math.ceil(this.width)
            const h = Math.ceil(this.height)
            return {
                width: w,
                height: h,
                style: {
                    width: `${w / overscale}px`,
                    height: `${h / overscale}px`,
                },
            }
        },
    },

    watch: {
        visible() {
            this.update()
        },

        fit() {
            this.update()
        },
    },

    async created() {
        this.update = debounce(this.update, 100)
        this.update()
        this.devicePixelRatio = window.devicePixelRatio
    },

    mounted() {
        this.listen("resize", () => {
            if (this.devicePixelRatio == window.devicePixelRatio) {
                this.update()
            }
            this.devicePixelRatio = window.devicePixelRatio
        })
    },

    methods: {
        async update() {
            if (this.visible) {
                await this.render()
            } else {
                await this.unrender()
            }
        },

        async render() {
            await semaphore.acquire()

            const page = await toRaw(this.pdf).getPage(this.number)
            const originalViewport = page.getViewport({ scale: 1 })

            const documentAspectRatio =
                originalViewport.width / originalViewport.height
            const bodyAspectRatio = this.bodyWidth / this.bodyHeight

            let scale = 1
            if (documentAspectRatio < bodyAspectRatio && this.fit == "height") {
                // fit to height
                scale = this.bodyHeight / originalViewport.height
            } else {
                // fit to width
                const pageElement = this.$refs.page
                if (!pageElement) {
                    semaphore.release()
                    return
                }
                scale = pageElement.clientWidth / originalViewport.width
            }

            const scaledViewport = page.getViewport({ scale })
            const overscaledViewport = page.getViewport({
                scale: scale * overscale,
            })

            this.width = overscaledViewport.width
            this.height = overscaledViewport.height

            const canvas = this.$refs.canvas
            if (!canvas) {
                semaphore.release()
                return
            }
            await page.render({
                canvasContext: canvas.getContext("2d"),
                viewport: overscaledViewport,
            }).promise

            const annotations = await page.getAnnotations()

            const container = this.$refs["data-layer"]
            if (!container) {
                semaphore.release()
                return
            }

            // Destroy previous annotations
            container.innerHTML = ""

            const textLayer = new this.pdfjs.TextLayer({
                textContentSource: await page.getTextContent(),
                container,
                viewport: scaledViewport,
            })

            const annotationLayer = new this.pdfjs.AnnotationLayer({
                div: container,
                page,
                viewport: scaledViewport.clone({ dontFlip: true }),
            })

            const bus = new Bus()
            bus.on("goto-page", pageRef => {
                this.$emit("goto-page", pageRef)
            })

            bus.on("goto-destination", destination => {
                this.$emit("goto-destination", destination)
            })

            await Promise.all([
                textLayer.render(),
                annotationLayer.render({
                    linkService: new LinkService({ bus }),
                    annotations,
                }),
            ])

            this.rendered = true

            semaphore.release()
        },

        unrender() {
            this.rendered = false
        },
    },

    components: {
        LoadingSpinner,
    },
}
</script>

<style>
/* These can't be scoped! */
.pdfjs__annotation-layer,
.pdfjs__text-layer {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    opacity: 0.2;
    line-height: 1;
}

.pdfjs__annotation-layer > section,
.pdfjs__text-layer > div,
.pdfjs__text-layer > span {
    color: transparent;
    position: absolute;
    white-space: pre;
    cursor: text;
    transform-origin: 0 0;
}

.pdfjs__annotation-layer > .linkAnnotation > a {
    position: absolute;
    font-size: 1em;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
</style>
